/**
 * How a picture could function inside an ad layout, as ingestion classified it.
 *
 * A closed vocabulary owned by guidelines-api (`MODEL_OWNED_TAG_KEYS` / `AD_USAGES`), mirrored here
 * only to label it. Anything outside the list resolves to undefined rather than being drawn raw: a
 * value this app has never heard of is a vocabulary change, and printing it verbatim would put an
 * untranslated wire string on screen.
 */
const AD_USAGE_LABEL_KEYS = {
  background: 'brandMedia.adUsage.background',
  product: 'brandMedia.adUsage.product',
  'subject-cutout': 'brandMedia.adUsage.subjectCutout',
  logo: 'brandMedia.adUsage.logo',
  'graphic-overlay': 'brandMedia.adUsage.graphicOverlay',
  other: 'brandMedia.adUsage.other',
} as const;

export type AdUsageLabelKey = (typeof AD_USAGE_LABEL_KEYS)[keyof typeof AD_USAGE_LABEL_KEYS];

/**
 * The one adUsage worth putting on a thumbnail, as a translation key.
 *
 * First value rather than all of them: the key can hold several, and a tile has room for one badge.
 * Ingestion writes the strongest reading first, so the first is the one to show.
 */
export function adUsageLabelKey(
  // The value is optional on purpose: an index signature otherwise types every lookup as present,
  // which makes the guard below read as dead code while it is the only thing standing between this
  // and a crash on a row that has no adUsage at all.
  tags: Readonly<Record<string, readonly string[] | undefined>> | undefined,
): AdUsageLabelKey | undefined {
  const primary = tags?.adUsage?.[0];
  if (primary === undefined) return undefined;

  return AD_USAGE_LABEL_KEYS[primary as keyof typeof AD_USAGE_LABEL_KEYS];
}
